import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../lib/api";
import { useDomains } from "../lib/domain-context";
import { Plus, Send, Pause, Play, X } from "lucide-react";

interface Campaign { id: string; name: string; status: string; totalRecipients: number; sentCount: number; createdAt: string; pauseReason: string | null }
interface Tpl { id: string; name: string }
interface List { id: string; name: string; contact_count: number }

const PAGE_SIZE = 25;

export default function CampaignsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ["campaigns", page],
    queryFn: () => api<{ campaigns: Campaign[]; total: number }>(`/api/campaigns?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`),
  });
  const [showNew, setShowNew] = useState(false);

  // Inline status mutations — invalidate the list query so the row
  // re-renders with the new status + button set.
  const launch = useMutation({
    mutationFn: (id: string) => api(`/api/campaigns/${id}/launch`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
  const pause = useMutation({
    mutationFn: (id: string) => api(`/api/campaigns/${id}/pause`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
  const resume = useMutation({
    mutationFn: (id: string) => api(`/api/campaigns/${id}/resume`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/api/campaigns/${id}/cancel`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });

  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">Campaigns</h1>
        <button onClick={() => setShowNew(true)}
          className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-indigo-700">
          <Plus size={14} /> New campaign
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">Sorted by most recent. Click a row name for details, or use the inline buttons to control sending state.</p>

      {launch.error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">Launch failed: {(launch.error as Error).message}</div>}

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Recipients</th>
              <th className="text-right px-4 py-2">Sent</th>
              <th className="text-left px-4 py-2">Created</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.campaigns ?? []).map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2"><Link to={`/campaigns/${c.id}`} className="text-indigo-600 hover:underline font-medium">{c.name}</Link></td>
                <td className="px-4 py-2"><StatusBadge s={c.status} reason={c.pauseReason} /></td>
                <td className="px-4 py-2 text-right">{c.totalRecipients}</td>
                <td className="px-4 py-2 text-right">{c.sentCount}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{new Date(c.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-2 text-right">
                  <RowActions
                    c={c}
                    onLaunch={() => launch.mutate(c.id)}
                    onPause={() => pause.mutate(c.id)}
                    onResume={() => resume.mutate(c.id)}
                    onCancel={() => { if (confirm(`Cancel "${c.name}"?`)) cancel.mutate(c.id); }}
                    busy={launch.isPending || pause.isPending || resume.isPending || cancel.isPending}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && data?.campaigns.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No campaigns yet.</p>
        )}

        {total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <div>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</div>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Prev</button>
              <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {showNew && <NewCampaign onClose={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ["campaigns"] }); }} />}
    </div>
  );
}

function StatusBadge({ s, reason }: { s: string; reason: string | null }) {
  const map: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700",
    scheduled: "bg-blue-100 text-blue-700",
    sending: "bg-amber-100 text-amber-700",
    paused: "bg-orange-100 text-orange-700",
    done: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
    cancelled: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${map[s] ?? "bg-gray-100 text-gray-700"}`}
      title={reason ? `Reason: ${reason}` : undefined}>
      {s}{reason === "daily_cap_reached" ? " (cap)" : ""}
    </span>
  );
}

function RowActions({ c, onLaunch, onPause, onResume, onCancel, busy }: {
  c: Campaign; onLaunch: () => void; onPause: () => void; onResume: () => void; onCancel: () => void; busy: boolean;
}) {
  const btn = "inline-flex items-center gap-1 text-xs px-2 py-1 rounded border";
  return (
    <div className="flex justify-end gap-1.5">
      {c.status === "draft" && (
        <button onClick={onLaunch} disabled={busy} className={`${btn} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}>
          <Send size={11} /> Launch
        </button>
      )}
      {c.status === "sending" && (
        <button onClick={onPause} disabled={busy} className={`${btn} border-amber-500 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50`}>
          <Pause size={11} /> Pause
        </button>
      )}
      {c.status === "paused" && (
        <button onClick={onResume} disabled={busy} className={`${btn} border-indigo-600 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50`}>
          <Play size={11} /> Resume
        </button>
      )}
      {(c.status === "sending" || c.status === "paused" || c.status === "scheduled") && (
        <button onClick={onCancel} disabled={busy} className={`${btn} border-gray-300 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50`}>
          <X size={11} /> Cancel
        </button>
      )}
    </div>
  );
}

function NewCampaign({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { domains, activeId, setActiveId } = useDomains();
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [listIds, setListIds] = useState<Set<string>>(new Set());
  const [throttle, setThrottle] = useState(3);
  const [pauseOnDailyCap, setPauseOnDailyCap] = useState(true);
  // Local override of the global DomainSwitcher selection — lets the
  // user compose a campaign for a domain different from the one they're
  // currently viewing stats for without changing their dashboard scope.
  const [composeDomainId, setComposeDomainId] = useState<string | null>(activeId);
  useEffect(() => { if (!composeDomainId) setComposeDomainId(activeId); }, [activeId, composeDomainId]);

  const tpls = useQuery({ queryKey: ["templates"], queryFn: () => api<{ templates: Tpl[] }>("/api/templates") });
  // Fetch ALL lists for the picker, not just the first page. A user is
  // capped at 30 lists (MAX_LISTS_PER_USER) and the backend limit maxes
  // at 100, so limit=100 always returns the complete set. Without an
  // explicit limit the endpoint defaults to 10 and the picker silently
  // hides lists past the first page.
  const lists = useQuery({ queryKey: ["lists", "all"], queryFn: () => api<{ lists: List[] }>("/api/lists?limit=100") });

  const mut = useMutation({
    mutationFn: () => api<{ campaign: { id: string } }>("/api/campaigns", {
      method: "POST",
      body: JSON.stringify({
        name, templateId, listIds: Array.from(listIds),
        notifyDomainId: composeDomainId,
        throttlePerMinute: throttle, pauseOnDailyCap,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      // If the user composed for a different domain than they were
      // viewing, switch their global selection so they land on the
      // matching dashboard / activity view.
      if (composeDomainId && composeDomainId !== activeId) setActiveId(composeDomainId);
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 mb-4">New campaign</h3>

        {domains.length > 1 && (
          <>
            <label className="block text-xs font-medium text-gray-600 mb-1">Send from domain</label>
            <select value={composeDomainId ?? ""} onChange={(e) => setComposeDomainId(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3">
              {domains.map((d) => (
                <option key={d.notifyDomainId} value={d.notifyDomainId}>
                  {d.senderLocalPart}@{d.senderDomain}
                </option>
              ))}
            </select>
          </>
        )}
        {domains.length === 1 && (
          <p className="text-xs text-gray-500 mb-3">
            Sends as <code className="bg-gray-50 px-1 rounded">{domains[0].senderLocalPart}@{domains[0].senderDomain}</code>
          </p>
        )}

        <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />

        <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3">
          <option value="">Select…</option>
          {tpls.data?.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <label className="block text-xs font-medium text-gray-600 mb-1">Lists</label>
        <div className="border border-gray-300 rounded p-2 mb-3 max-h-32 overflow-y-auto">
          {lists.data?.lists.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-sm py-0.5">
              <input type="checkbox" checked={listIds.has(l.id)} onChange={(e) => {
                const s = new Set(listIds); e.target.checked ? s.add(l.id) : s.delete(l.id); setListIds(s);
              }} />
              {l.name} <span className="text-gray-400 text-xs">({l.contact_count})</span>
            </label>
          ))}
          {lists.data?.lists.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No lists yet — create one first.</p>}
        </div>

        <label className="block text-xs font-medium text-gray-600 mb-1">Throttle (emails per minute)</label>
        <input type="number" min={1} max={1000} value={throttle} onChange={(e) => setThrottle(parseInt(e.target.value) || 3)}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-1" />
        <p className="text-xs text-gray-400 mb-4">Recipients are spaced out over time. 3/min = one every 20s · 30/min = one every 2s. The workspace also has a daily cap (see Settings).</p>

        <label className="flex items-start gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={pauseOnDailyCap} onChange={(e) => setPauseOnDailyCap(e.target.checked)}
            className="mt-0.5" />
          <span className="text-xs text-gray-700">
            <span className="font-medium block mb-0.5">Auto-pause when daily limit is hit</span>
            <span className="text-gray-500">Recommended. Campaign visibly pauses with reason "daily cap reached"; remaining sends auto-resume after midnight UTC. Uncheck only for fire-and-forget sends spread over multiple days.</span>
          </span>
        </label>

        {mut.error && <p className="text-sm text-red-600 mb-2">{(mut.error as Error).message}</p>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
          <button onClick={() => mut.mutate()} disabled={!name || !templateId || listIds.size === 0 || mut.isPending}
            className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
            {mut.isPending ? "Creating…" : "Create draft"}
          </button>
        </div>
      </div>
    </div>
  );
}
